import { motion } from "framer-motion";
import { Camera, FileText, Wrench, CheckCircle2, MapPin, Hammer, MessageSquare, Boxes } from "lucide-react";
import { MaskHeading, Reveal, EASE } from "@/components/site/motion";

const STEPS = [
  { icon: Camera, t: "Send details & photos", d: "Tell us what you bought and snap a few photos of the boxes or the space." },
  { icon: FileText, t: "Get a clear quote", d: "We review the job and reply with a straightforward quote before any work starts." },
  { icon: Wrench, t: "We show up & build", d: "We arrive with our own tools and assemble or install it on site." },
  { icon: CheckCircle2, t: "Ready to use", d: "Built square, tight and level — ready for you to enjoy." },
];

const WHY = [
  { icon: MapPin, t: "Local to Oxford County", d: "Based in Woodstock and serving neighbours in Ingersoll and surrounding towns." },
  { icon: Boxes, t: "One call, nearly any job", d: "Indoor, outdoor, small or large — one team for the whole list." },
  { icon: Hammer, t: "Our tools, our hands", d: "You don't need to own a drill. We bring what the job needs." },
  { icon: MessageSquare, t: "Upfront communication", d: "Quote first, by phone, text, email or online — whatever's easiest for you." },
];

export const Process = () => (
  <section className="relative bg-[var(--ink)] py-24 lg:py-36" data-testid="process-section">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
      <p className="eyebrow mb-5">How it works</p>
      <MaskHeading lines={[{ text: "Four steps." }, { text: "Zero manuals.", className: "text-[var(--orange)]" }]}
        className="font-display font-black uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.88] mb-16" />
      <div className="grid gap-px bg-[var(--line)] border border-[var(--line)] sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => (
          <motion.div key={s.t} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: EASE, delay: i * 0.12 }}
            className="group relative bg-[var(--ink)] p-8 lg:p-10 min-h-[300px] flex flex-col justify-between overflow-hidden hover:bg-[var(--char)] transition-colors duration-500"
            data-testid={`process-step-${i + 1}`}>
            <span className="absolute -right-3 -top-6 font-display font-black text-[9rem] leading-none text-white/[0.04] group-hover:text-[var(--orange)]/[0.12] transition-colors duration-500">{i + 1}</span>
            <s.icon className="w-9 h-9 text-[var(--orange)]" strokeWidth={1.75} />
            <div>
              <p className="font-mono-x text-xs text-zinc-400 mb-3">STEP 0{i + 1}</p>
              <h3 className="font-display font-black uppercase text-3xl leading-none">{s.t}</h3>
              <p className="mt-3 text-zinc-300 text-sm leading-relaxed">{s.d}</p>
            </div>
            <span className="absolute bottom-0 left-0 h-[3px] w-0 bg-[var(--orange)] group-hover:w-full transition-[width] duration-700" />
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const WhyUs = () => (
  <section className="relative bg-[var(--char)] py-24 lg:py-36 border-t border-[var(--line)] overflow-hidden" data-testid="why-us-section">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <p className="eyebrow mb-5">Why Assemble Pro</p>
        <MaskHeading lines={[{ text: "Built right." }, { text: "The first time." }]}
          className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]" />
        <Reveal className="mt-10 relative clip-cut overflow-hidden aspect-[4/3]">
          <img src="/img/tools.jpg" alt="Work gloves, screwdriver and tape measures ready for an assembly job" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[var(--ink)]/70 to-transparent" />
        </Reveal>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5 content-center">
        {WHY.map((w, i) => (
          <Reveal key={w.t} delay={i * 0.08}>
            <div className="group h-full border border-[var(--line)] bg-[var(--ink)] p-7 hover:border-[var(--orange)] transition-colors duration-500" data-testid={`why-card-${i + 1}`}>
              <div className="w-12 h-12 grid place-items-center bg-[var(--orange)]/10 border border-[var(--orange)]/30 group-hover:bg-[var(--orange)] transition-colors duration-500">
                <w.icon className="w-5 h-5 text-[var(--orange)] group-hover:text-[var(--ink)] transition-colors duration-500" />
              </div>
              <h3 className="mt-6 font-display font-black uppercase text-2xl leading-none">{w.t}</h3>
              <p className="mt-3 text-zinc-300 text-sm leading-relaxed">{w.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
